import React, { useState } from "react";
import { useCollection, api, fmtDate, fmtTime } from "@/lib/caesar";
import { PageHeader, Loading, EmptyState, Labeled, Panel } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, MapPin, Users, Sparkles, ClipboardCheck, CalendarDays } from "lucide-react";

const empty = { title: "", time_start: "", time_end: "", location: "", participants: "", notes: "" };

export default function CalendarPage() {
  const { items, loading, create, update, remove } = useCollection("events");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(empty);
  const [prepare, setPrepare] = useState(null);
  const [result, setResult] = useState(null);
  const [resForm, setResForm] = useState({ what_happened: "", agreed: "", who_owes: "", next: "", followup_date: "" });

  const save = async () => {
    if (!form.title.trim() || !form.time_start) { toast.error("Title and time required"); return; }
    await create({ ...form, time_start: new Date(form.time_start).toISOString(), time_end: form.time_end ? new Date(form.time_end).toISOString() : "" });
    toast.success("Event added"); setDlg(false); setForm(empty);
  };

  const saveResult = async (ev) => {
    await api.post("/meeting_notes", { event_id: ev.id, event_title: ev.title, ...resForm });
    if (resForm.next) {
      await api.post("/tasks", { title: resForm.next, owner: "", status: "todo", priority_label: "high", deadline: resForm.followup_date ? new Date(resForm.followup_date).toISOString() : "", notes: `From meeting: ${ev.title}` });
    }
    toast.success("Meeting result saved" + (resForm.next ? " · task created" : ""));
    setResult(null); setResForm({ what_happened: "", agreed: "", who_owes: "", next: "", followup_date: "" });
  };

  if (loading) return <Loading />;
  const sorted = [...items].sort((a, b) => (a.time_start || "").localeCompare(b.time_start || ""));

  return (
    <div className="space-y-6">
      <PageHeader title="Calendar" subtitle="Meetings, prep, and results" testId="calendar-header"
        action={<button data-testid="event-new-btn" onClick={() => { setForm(empty); setDlg(true); }} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-gold text-ink font-bold"><Plus size={16} /> Add Event</button>} />

      <Panel className="p-4 flex items-center gap-3 border-gold/20">
        <CalendarDays className="text-gold" size={18} />
        <p className="text-sm text-[#A3A3A3]">Google Calendar sync is prepared in <span className="text-gold">Connections</span>. For now, add events manually.</p>
      </Panel>

      {sorted.length === 0 ? <EmptyState testId="calendar-empty" title="No meetings scheduled." hint="Add an event to prepare and record results." /> : (
        <div className="space-y-3">
          {sorted.map((e) => (
            <div key={e.id} data-testid={`event-${e.id}`} className="bg-surface border border-edge rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold">{e.title}</p>
                  <p className="font-num text-xs text-gold mt-0.5">{fmtDate(e.time_start)} · {fmtTime(e.time_start)}{e.time_end ? `–${fmtTime(e.time_end)}` : ""}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#A3A3A3]">
                    {e.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {e.location}</span>}
                    {e.participants && <span className="inline-flex items-center gap-1"><Users size={12} /> {e.participants}</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button data-testid={`event-prepare-${e.id}`} onClick={() => setPrepare(e)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] hover:text-[#FAFAFA]"><Sparkles size={13} /> Prepare Me</button>
                <button data-testid={`event-result-${e.id}`} onClick={() => setResult(e)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-edge text-xs font-semibold text-[#A3A3A3] hover:text-[#FAFAFA]"><ClipboardCheck size={13} /> Meeting Result</button>
                <button onClick={async () => { await remove(e.id); }} className="text-xs text-[#A3A3A3] hover:text-sred px-2">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add event */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-surface border-edge max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">Add Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Labeled label="Title"><Input data-testid="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Start"><Input data-testid="event-start" type="datetime-local" value={form.time_start} onChange={(e) => setForm({ ...form, time_start: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="End"><Input type="datetime-local" value={form.time_end} onChange={(e) => setForm({ ...form, time_end: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Participants"><Input value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} className="bg-ink border-edge" /></Labeled>
            </div>
            <Labeled label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-ink border-edge" /></Labeled>
            <button data-testid="event-save" onClick={save} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Save Event</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prepare Me */}
      <Dialog open={!!prepare} onOpenChange={(v) => !v && setPrepare(null)}>
        <DialogContent className="bg-surface border-edge max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">Prepare Me</DialogTitle></DialogHeader>
          {prepare && (
            <div className="space-y-2 text-sm">
              <Row label="Meeting" value={prepare.title} />
              <Row label="Time" value={`${fmtDate(prepare.time_start)} · ${fmtTime(prepare.time_start)}`} />
              <Row label="Who is attending" value={prepare.participants || "—"} />
              <Row label="Location" value={prepare.location || "—"} />
              <Row label="Previous notes" value={prepare.notes || "—"} />
              <div className="pt-2">
                <span className="label-caps">Three recommended objectives</span>
                <ul className="list-disc list-inside text-[#A3A3A3] mt-1 space-y-1">
                  <li>Confirm the desired outcome and who owns the next move.</li>
                  <li>Surface any money or open items tied to this relationship.</li>
                  <li>Agree a clear follow-up date before leaving.</li>
                </ul>
              </div>
              <p className="text-xs text-[#5a5a5a] pt-1">Caesar only uses information stored here. Add notes and links to this event to enrich preparation.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Meeting Result */}
      <Dialog open={!!result} onOpenChange={(v) => !v && setResult(null)}>
        <DialogContent className="bg-surface border-edge max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">Add Meeting Result</DialogTitle></DialogHeader>
          {result && (
            <div className="space-y-3">
              <Labeled label="What happened?"><Textarea value={resForm.what_happened} onChange={(e) => setResForm({ ...resForm, what_happened: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="What was agreed?"><Input value={resForm.agreed} onChange={(e) => setResForm({ ...resForm, agreed: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <Labeled label="Who owes what?"><Input value={resForm.who_owes} onChange={(e) => setResForm({ ...resForm, who_owes: e.target.value })} className="bg-ink border-edge" /></Labeled>
              <div className="grid grid-cols-2 gap-3">
                <Labeled label="What happens next? (creates task)"><Input data-testid="result-next" value={resForm.next} onChange={(e) => setResForm({ ...resForm, next: e.target.value })} className="bg-ink border-edge" /></Labeled>
                <Labeled label="Follow-up date"><Input type="date" value={resForm.followup_date} onChange={(e) => setResForm({ ...resForm, followup_date: e.target.value })} className="bg-ink border-edge" /></Labeled>
              </div>
              <button data-testid="result-save" onClick={() => saveResult(result)} className="w-full h-11 rounded-lg bg-gold text-ink font-bold">Save Result</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-edge/50"><span className="label-caps">{label}</span><span className="text-[#FAFAFA] text-right">{value}</span></div>
);
